import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { createSetupIntent } from '@yoimo/client-sdk/subscriptions';
import {
  BuySubscriptionRequest,
  BuyTicketRequest,
  PriceWithTaxCalculation,
  StripeAddress,
} from '@yoimo/interfaces';
import {
  BrandingService,
  ButtonModule,
  IconModule,
  ToastService,
  UIState,
  WindowService,
} from '@yoimo/joymo-ui';

import {
  Appearance,
  Stripe,
  StripeElements,
  loadStripe,
  StripeAddressElementOptions,
  StripeAddressElement,
  StripePaymentElement,
} from '@stripe/stripe-js';
import {
  Observable,
  ReplaySubject,
  catchError,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { logger, ScopeService } from '@core/services';

import {
  AdditionalPaymentConfig,
  PlanOption,
  TicketOption,
} from '../../ticket-options/ticket-options-types';

type StripeContext = {
  stripe: Stripe;
  elements: StripeElements;
};

type SetupIntent = {
  publicKey: string;
  clientSecret: string;
  customerSavedAddress?: StripeAddress;
};

export type PaymentDetails = {
  paymentId: string;
  billingAddress: StripeAddress;
};

function stripeAddressToAddressDefaultValues(
  sa: StripeAddress
): StripeAddressElementOptions['defaultValues'] {
  const { fullName, postalCode, ...rest } = sa;
  return {
    address: { ...rest, postal_code: postalCode },
    name: fullName,
  };
}

@Component({
  selector: 'joymo-capture-payment-method',
  standalone: true,
  templateUrl: './capture-payment-method.component.html',
  imports: [
    ButtonModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IconModule,
  ],
})
export class CapturePaymentMethodComponent implements OnInit {
  @Input() title?: string;
  @Input() option?: {
    option: TicketOption | PlanOption;
    additionalConfig: AdditionalPaymentConfig;
  };
  readonly confirmButtonLabel = input<string>('Confirm');
  @Output() onPaymentDetailsCaptured = new EventEmitter<PaymentDetails>();
  /**
   * If option has been provided, will emit an event containing the full price calculation with
   * tax.
   */
  @Output() onPriceEstimateAvailable =
    new EventEmitter<PriceWithTaxCalculation>();

  @ViewChild('stripePaymentElements', { static: false })
  private readonly stripePaymentContainer?: ElementRef;

  @ViewChild('stripeAddressElements', { static: false })
  private readonly stripeAddressContainer?: ElementRef;

  private readonly _setupIntent$ = new ReplaySubject<SetupIntent>(1);

  readonly stripeConfig$: Observable<StripeContext>;
  readonly onSubmit = new EventEmitter<void>();
  /**
   * Current input state; starts as undefined to wait until Stripe is loaded
   */
  state = signal<UIState | undefined>(undefined);
  loading = computed(() => this.state() === 'loading');
  loaded = computed(() => this.state() !== undefined);
  submitDisabled = signal<boolean>(true);

  constructor(
    private ff: Functions,
    private brandingService: BrandingService,
    private windowService: WindowService,
    private toastService: ToastService,
    private scopeService: ScopeService
  ) {
    this.stripeConfig$ = this._setupIntent$
      .asObservable()
      .pipe(switchMap((i) => this.setupStripe$(i)));
  }

  ngOnInit(): void {
    this.getSetupIntent();

    this.onSubmit
      .pipe(
        filter(() => !this.submitDisabled()),
        tap((_) => this.state.set('loading')),
        withLatestFrom(this.stripeConfig$),
        switchMap(([_, state]) => this.confirmSetupIntent$(state))
      )
      .subscribe((result) => {
        if (!result) {
          this.state.set('error');
          this.getSetupIntent();
          return;
        }
        this.submitDisabled.set(true);
        this.onPaymentDetailsCaptured.emit({
          paymentId: result.paymentId,
          billingAddress: result.address,
        });
      });
  }

  /**
   * Fetch a new SetupIntent. Can be observed through `setupIntent$`
   *
   * @remarks SetupIntents are single-use, so this method can be accessed by
   * parent components in case a payment method is captured successfully
   * but an error occurs further down the line
   */
  async getSetupIntent(): Promise<void> {
    let priceEstimateRequest:
      | Omit<BuyTicketRequest, 'paymentMethodId'>
      | Omit<BuySubscriptionRequest, 'paymentMethodId'>
      | undefined = undefined;
    if (this.option && this.option.option.type === 'TICKET') {
      priceEstimateRequest = this.getBuyTicketRequestParams(this.option.option);
    }
    const response = await firstValueFrom(
      createSetupIntent(
        this.ff,
        this.scopeService.getChannel().docId,
        priceEstimateRequest
      )
    );
    if (!response.success) {
      logger.logCriticalError(response.errorReason, 'PAYMENTS');
      throw new Error('Failed to setup an intent');
    }
    if (response.priceEstimate) {
      this.onPriceEstimateAvailable.emit(response.priceEstimate);
    }
    this._setupIntent$.next({
      ...response.intent,
      customerSavedAddress: response.customerAddress,
    });
  }

  getBuyTicketRequestParams(
    ticket: TicketOption
  ): Omit<BuyTicketRequest, 'paymentMethodId'> {
    if (!ticket.eventId)
      throw new TypeError('eventId should be specified in the ticket');
    return {
      channelId: this.scopeService.getChannel().docId,
      eventId: ticket.eventId,
      ticketId: ticket.ticketData.id,
      selectedCurrency: ticket.optionPrice?.currency,
      // TODO: remove this
      dryRun: true,
    };
  }

  /**
   * Given an already configured Stripe state, setup and return a payment method.
   */
  /* istanbul ignore next */
  private confirmSetupIntent$(
    state: StripeContext
  ): Observable<{ paymentId: string; address: StripeAddress } | null> {
    this.state.set('loading');

    return from(
      state.stripe.confirmSetup({
        elements: state.elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: this.windowService.document.location.href,
        },
      })
    ).pipe(
      switchMap(async (result) => {
        if (result.error) {
          throw new Error(result.error.message);
        }
        const addressElement = state.elements.getElement('address');

        const addressValue = await addressElement?.getValue();
        let capturedAddress =
          addressValue && addressValue.complete ? addressValue.value : null;

        if (!capturedAddress) {
          throw new Error('Address not provided');
        }

        return {
          paymentId: result.setupIntent?.payment_method as string,
          address: {
            fullName: capturedAddress.name,
            city: capturedAddress.address.city,
            line1: capturedAddress.address.line1,
            line2: capturedAddress.address.line2 || '',
            country: capturedAddress.address.country,
            postalCode: capturedAddress.address.postal_code,
            state: capturedAddress.address.state,
          } satisfies StripeAddress,
        };
      }),
      catchError((e: Error) => {
        this.toastService.open(
          e.message ||
            $localize`:@@capturePaymentFailedToastMessage:Operation failed`,
          {
            type: 'error',
          }
        );
        return of(null);
      })
    );
  }

  private listenToStripeElementsEvents(
    payment: StripePaymentElement,
    address: StripeAddressElement
  ): void {
    let addressOk = false;
    let paymentOk = false;

    const postChange = () => {
      this.submitDisabled.set(!addressOk || !paymentOk);
    };
    payment.on('change', (event) => {
      paymentOk = event.complete;
      postChange();
    });

    address.on('change', (event) => {
      addressOk = event.complete;
      postChange();
    });
  }

  protected setupStripe$(intent: SetupIntent): Observable<StripeContext> {
    return from(loadStripe(intent.publicKey)).pipe(
      map((stripe) => ({ stripe, intent })),
      switchMap((response) => {
        if (!response.stripe) {
          throw new Error('Unable to load stripe');
        }
        const elements = response.stripe?.elements({
          appearance: this.getStripeInputStyle(),
          clientSecret: response.intent.clientSecret,
        });
        const payment = elements.create('payment');
        payment.mount(this.stripePaymentContainer?.nativeElement);

        const address = elements.create('address', {
          mode: 'billing',
          defaultValues: !response.intent.customerSavedAddress
            ? undefined
            : stripeAddressToAddressDefaultValues(
                response.intent.customerSavedAddress
              ),
        });
        address.mount(this.stripeAddressContainer?.nativeElement);

        this.listenToStripeElementsEvents(payment, address);

        this.state.set('idle');

        return of({ stripe: response.stripe, elements });
      })
    );
  }

  private getStripeInputStyle(): Appearance {
    const themeSelect = <T>(onDarkThemeValue: T, onLightThemeValue: T): T =>
      this.brandingService.themeMode === 'dark'
        ? onDarkThemeValue
        : onLightThemeValue;
    const theme = this.brandingService.getTheme();
    const colors = {
      darkgrey: '#000000',
      darkgrey10: '#0000001a',
      darkgrey40: '#00000066',
      darkgrey80: '#000000cc',
      white: '#ffffff',
      white20: '#ffffff33',
      white50: '#ffffff80',
    };

    return {
      theme: 'stripe',
      variables: {
        // Elements supports custom fonts by passing the 'fonts' option to the Elements group
        // Define Proxima font with custom font
        // https://stripe.com/docs/js/appendix/custom_font_source_object
        //fontFamily: {},

        // Can be either '8px' for rounded style or 'none'
        borderRadius: theme.themeStyle === 'rounded' ? '8px' : '0',
        colorBackground: theme.backgroundColor,
        // Dark-grey-80 for light theme, White for any dark theme
        colorText: themeSelect(colors.white, colors.darkgrey80),
        // Dark-grey-40 for light theme, White-50 for dark one
        colorTextPlaceholder: themeSelect(colors.white50, colors.darkgrey40),
        // Although this color is used for cards only, and we don't have any
        // we can still provide this color
        colorPrimary: theme.accentColor,
        // Red for light theme, Red-bright for dark one
        colorDanger: this.brandingService.redColor || '',
        // Dark-grey-40 for light theme, White-50 for dark one
        colorIcon: themeSelect(colors.white50, colors.darkgrey40),
      },

      rules: {
        // Certain properties don't allow RGBA values so they're defaulted to solid colors
        '.Input': {
          borderColor: themeSelect(colors.white20, colors.darkgrey10),
          boxShadow: 'none',
          color: themeSelect(colors.white, colors.darkgrey),
        },
        '.Input::placeholder': {
          color: 'lightgrey',
        },
        '.Input:focus': {
          borderColor: themeSelect(colors.white50, colors.darkgrey40),
          // White for any light theme, Background color for any dark theme
          backgroundColor: 'transparent',
        },
      },
    };
  }
}
